import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../../components/Layout';
import DataTable from '../../components/DataTable';
import accountTypesAPI from '../../api/accountTypesAPI';

const EMPTY_FORM = {
  name: '',
  code: '',
  category: '',
  normalBalance: 'debit',
  description: '',
  displayOrder: 0,
  isActive: true,
};

const ChartOfAccountsTypeMaintenancePage = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [types, setTypes] = useState([]);
  const [enums, setEnums] = useState({
    category: [],
    normalBalance: ['debit', 'credit'],
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const [typesRes, enumsRes] = await Promise.all([
        accountTypesAPI.getAll(),
        accountTypesAPI.getEnums(),
      ]);

      setTypes(Array.isArray(typesRes?.data) ? typesRes.data : []);
      const enumData = enumsRes?.data || {};
      setEnums({
        category: enumData.category || [],
        normalBalance: enumData.normalBalance || ['debit', 'credit'],
      });
    } catch (err) {
      setError(err?.message || String(err) || 'Failed to load chart of accounts types');
      setTypes([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const filteredData = useMemo(() => {
    if (!searchTerm) return types;
    const q = searchTerm.toLowerCase();
    return types.filter((row) =>
      [row.name, row.code, row.category, row.description]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(q))
    );
  }, [types, searchTerm]);

  const categoriesForMain = useMemo(() => {
    return enums?.category || [];
  }, [enums]);

  const openCreate = () => {
    setEditing(null);
    setFormData(EMPTY_FORM);
    setModalOpen(true);
    setError('');
  };

  const openEdit = (row) => {
    setEditing(row);
    setFormData({
      name: row.name || '',
      code: row.code || '',
      category: row.category || '',
      normalBalance: row.normalBalance || 'debit',
      description: row.description || '',
      displayOrder: Number.isFinite(Number(row.displayOrder)) ? Number(row.displayOrder) : 0,
      isActive: row.isActive !== false,
    });
    setModalOpen(true);
    setError('');
  };

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
    setEditing(null);
    setFormData(EMPTY_FORM);
  };

  const handleFieldChange = (event) => {
    const { name, value, type, checked } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSave = async (event) => {
    event.preventDefault();

    if (!formData.name || !formData.code || !formData.category) {
      setError('Name, code, and category are required.');
      return;
    }

    try {
      setSaving(true);
      setError('');

      const payload = {
        ...formData,
        code: formData.code.toUpperCase(),
        displayOrder: Number(formData.displayOrder || 0),
      };

      if (editing?._id) {
        await accountTypesAPI.update(editing._id, payload);
      } else {
        await accountTypesAPI.create(payload);
      }

      await loadData();
      closeModal();
    } catch (err) {
      setError(err?.message || String(err) || 'Failed to save chart of accounts type');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    const ok = window.confirm(`Delete type "${row.name}" (${row.code})?`);
    if (!ok) return;

    try {
      setError('');
      await accountTypesAPI.delete(row._id);
      await loadData();
    } catch (err) {
      setError(err?.message || String(err) || 'Failed to delete chart of accounts type');
    }
  };

  const columns = [
    {
      title: 'Code',
      key: 'code',
      render: (row) => <span className="font-mono text-sm">{row.code}</span>,
    },
    {
      title: 'Name',
      key: 'name',
      render: (row) => <span className="font-medium">{row.name}</span>,
    },
    {
      title: 'Category',
      key: 'category',
      render: (row) => <span className="capitalize">{String(row.category || '').replace(/-/g, ' ')}</span>,
    },
    {
      title: 'Normal Balance',
      key: 'normalBalance',
      render: (row) => <span className="uppercase text-xs">{row.normalBalance}</span>,
    },
    {
      title: 'Status',
      key: 'status',
      render: (row) => (
        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${row.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'}`}>
          {row.isActive ? 'Active' : 'Inactive'}
        </span>
      ),
    },
  ];

  return (
    <Layout pageTitle="Chart of Accounts Types Maintenance">
      <div className="bg-gray-100 min-h-screen p-6">
        {error && (
          <div className="bg-red-100 border border-red-300 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
          <input
            type="text"
            placeholder="Search by code, name, category..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 w-full md:max-w-md"
          />

          <button
            onClick={openCreate}
            className="transition-all duration-300 px-4 py-2 bg-teal-500 text-white rounded hover:bg-teal-600"
          >
            + New Type
          </button>
        </div>

        <DataTable
          columns={columns}
          data={filteredData}
          loading={loading}
          emptyMessage="No chart of accounts types found"
          onEdit={openEdit}
          onDelete={handleDelete}
        />

        {modalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl">
              <div className="p-6">
                <h2 className="text-xl font-semibold mb-4">
                  {editing ? 'Edit Chart of Accounts Type' : 'New Chart of Accounts Type'}
                </h2>

                <form onSubmit={handleSave}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-semibold mb-1">Code *</label>
                      <input
                        type="text"
                        name="code"
                        value={formData.code}
                        onChange={handleFieldChange}
                        className="w-full border rounded px-3 py-2"
                        maxLength={6}
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold mb-1">Name *</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleFieldChange}
                        className="w-full border rounded px-3 py-2"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold mb-1">Category *</label>
                      <select
                        name="category"
                        value={formData.category}
                        onChange={handleFieldChange}
                        className="w-full border rounded px-3 py-2"
                        required
                      >
                        <option value="">Select category</option>
                        {(enums.category || []).map((item) => (
                          <option key={item} value={item}>
                            {item.charAt(0).toUpperCase() + item.slice(1)}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold mb-1">Normal Balance</label>
                      <select
                        name="normalBalance"
                        value={formData.normalBalance}
                        onChange={handleFieldChange}
                        className="w-full border rounded px-3 py-2"
                      >
                        {(enums.normalBalance || ['debit', 'credit']).map((item) => (
                          <option key={item} value={item}>
                            {item}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold mb-1">Display Order</label>
                      <input
                        type="number"
                        name="displayOrder"
                        value={formData.displayOrder}
                        onChange={handleFieldChange}
                        className="w-full border rounded px-3 py-2"
                        min="0"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-semibold mb-1">Description</label>
                      <textarea
                        name="description"
                        value={formData.description}
                        onChange={handleFieldChange}
                        rows={3}
                        className="w-full border rounded px-3 py-2"
                      />
                    </div>

                    <div className="md:col-span-2 flex items-center gap-2">
                      <input
                        id="isActive"
                        type="checkbox"
                        name="isActive"
                        checked={formData.isActive}
                        onChange={handleFieldChange}
                      />
                      <label htmlFor="isActive" className="text-sm">Active</label>
                    </div>
                  </div>

                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={closeModal}
                      className="px-4 py-2 border border-gray-300 rounded"
                      disabled={saving}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-teal-500 text-white rounded hover:bg-teal-600 disabled:opacity-70"
                      disabled={saving}
                    >
                      {saving ? 'Saving...' : editing ? 'Update' : 'Create'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ChartOfAccountsTypeMaintenancePage;
